import { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, message, Collapse, Space, Tag, Popconfirm, Statistic, Row, Col } from 'antd';
import { DeleteOutlined, ScanOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';

const API = import.meta.env.VITE_API_BASE || '/api';
const token = () => localStorage.getItem('ar_token');
const h = () => ({ Authorization: 'Bearer ' + token() });

const CATEGORY_LABELS = {
  asign_docs: '签署文档 (asign_docs)',
  signed_docs: '已签文档 (signed_docs)',
  stamps: '印章图片 (stamps)',
  personal_seals: '个人印章 (personal_seals)',
  letters: '委托书附件 (letters)',
  seals: '公司印章 (seals)',
};

export default function SystemCleanup() {
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState({});

  const fetchScan = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(API + '/admin/cleanup/scan', { headers: h() });
      if (r.data.success) {
        setCategories(r.data.categories || []);
        setSummary(r.data.summary || null);
        setSelectedKeys({});
        const total = r.data.summary?.total_orphans || 0;
        if (total === 0) {
          message.success('扫描完成，没有发现孤文件');
        } else {
          message.success(`扫描完成，发现 ${total} 个孤文件`);
        }
      }
    } catch (e) {
      message.error('扫描失败：' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchScan();
  }, [fetchScan]);

  const handleSelect = (catKey, selectedRowKeys) => {
    setSelectedKeys(prev => ({ ...prev, [catKey]: selectedRowKeys }));
  };

  const handleSelectAll = (catKey, files) => {
    setSelectedKeys(prev => {
      const current = prev[catKey] || [];
      const allPaths = files.map(f => f.path);
      if (current.length === allPaths.length) {
        return { ...prev, [catKey]: [] };
      }
      return { ...prev, [catKey]: allPaths };
    });
  };

  const handleDelete = async () => {
    const allFiles = [];
    Object.values(selectedKeys).forEach(keys => {
      allFiles.push(...keys);
    });

    if (allFiles.length === 0) {
      message.warning('请先选择要清理的文件');
      return;
    }

    setDeleting(true);
    try {
      const r = await axios.post(API + '/admin/cleanup/delete', { files: allFiles }, { headers: h() });
      if (r.data.success) {
        message.success(`成功删除 ${r.data.deleted} 个文件${r.data.failed > 0 ? `，${r.data.failed} 个失败` : ''}`);
        // 刷新列表
        await fetchScan();
      }
    } catch (e) {
      message.error('删除失败：' + (e.response?.data?.detail || e.message));
    } finally {
      setDeleting(false);
    }
  };

  const totalSelected = Object.values(selectedKeys).reduce((sum, keys) => sum + keys.length, 0);

  const categoryColumns = (catKey, files) => [
    {
      title: '文件名',
      dataIndex: 'filename',
      key: 'filename',
      ellipsis: true,
    },
    {
      title: '大小',
      dataIndex: 'size_display',
      key: 'size_display',
      width: 100,
    },
  ];

  const collapseItems = categories
    .filter(cat => cat.orphan_files && cat.orphan_files.length > 0)
    .map(cat => {
      const catKey = cat.category;
      const files = cat.orphan_files;
      return {
        key: catKey,
        label: (
          <Space>
            <span>{CATEGORY_LABELS[catKey] || catKey}</span>
            <Tag color="orange">{files.length} 个孤文件</Tag>
            <Tag color="blue">{cat.total_size_display}</Tag>
          </Space>
        ),
        children: (
          <div>
            <Button
              size="small"
              type="link"
              onClick={() => handleSelectAll(catKey, files)}
              style={{ marginBottom: 8 }}
            >
              {(selectedKeys[catKey] || []).length === files.length ? '取消全选' : '全选'}
            </Button>
            <Table
              size="small"
              rowKey="path"
              columns={categoryColumns(catKey, files)}
              dataSource={files}
              rowSelection={{
                selectedRowKeys: selectedKeys[catKey] || [],
                onChange: (keys) => handleSelect(catKey, keys),
              }}
              pagination={files.length > 20 ? { pageSize: 20, showSizeChanger: true } : false}
            />
          </div>
        ),
      };
    });

  const emptyCategories = categories.filter(cat => !cat.orphan_files || cat.orphan_files.length === 0);
  const hasOrphans = categories.some(cat => cat.orphan_files && cat.orphan_files.length > 0);

  return (
    <div>
      <Card
        title="数据清理"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchScan} loading={loading}>
              重新扫描
            </Button>
            {hasOrphans && (
              <Popconfirm
                title={
                  <div>
                    <p>确定要删除选中的 <b>{totalSelected}</b> 个文件吗？</p>
                    <p style={{ color: '#ff4d4f', fontSize: 12 }}>此操作不可恢复！</p>
                  </div>
                }
                onConfirm={handleDelete}
                okText="确认删除"
                cancelText="取消"
                okButtonProps={{ danger: true }}
              >
                <Button
                  type="primary"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deleting}
                  disabled={totalSelected === 0}
                >
                  清理选中文件 ({totalSelected})
                </Button>
              </Popconfirm>
            )}
          </Space>
        }
      >
        {/* 汇总统计 */}
        {summary && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Statistic
                title="孤文件总数"
                value={summary.total_orphans}
                valueStyle={{ color: '#faad14' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="可释放空间"
                value={summary.total_size_display}
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="目录分类"
                value={categories.filter(c => (c.orphan_files || []).length > 0).length}
                suffix={`/ ${categories.length}`}
              />
            </Col>
          </Row>
        )}

        {/* 孤文件列表 */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>正在扫描文件…</div>
        ) : hasOrphans ? (
          <Collapse
            defaultActiveKey={categories.filter(c => c.orphan_files?.length > 0).map(c => c.category)}
            items={collapseItems}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            <ScanOutlined style={{ fontSize: 48, marginBottom: 16, display: 'block' }} />
            所有文件均已关联，没有发现孤文件
          </div>
        )}

        {/* 干净分类 */}
        {emptyCategories.length > 0 && (
          <div style={{ marginTop: 12 }}>
            {emptyCategories.map(cat => (
              <Tag key={cat.category} color="green" style={{ marginBottom: 4 }}>
                {CATEGORY_LABELS[cat.category] || cat.category}: 干净 ✓
              </Tag>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
